import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Clock, Loader2 } from "lucide-react";
import { fetchArticles } from "@/lib/api";

export default function Knowledge() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    setLoading(true);
    fetchArticles({ q, category: cat })
      .then(setData)
      .finally(() => setLoading(false));
  }, [q, cat]);

  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid="page-knowledge">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl">
          <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">Knowledge Centre</Badge>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            Insurance & investing, <span className="text-gradient-blue">in plain English.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed text-lg">
            Short, practical articles to help you make better financial decisions.
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="knowledge-search"
              placeholder="Search articles…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", ...data.categories].map((c) => (
              <button
                key={c}
                data-testid={`knowledge-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-3.5 py-2 rounded-full text-xs font-medium border transition ${
                  cat === c ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <p className="text-center py-20 text-slate-500">No articles match your search.</p>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.items.map((a) => (
              <Link
                key={a.slug}
                to={`/knowledge/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="group rounded-2xl border border-slate-100 bg-white p-6 hover:-translate-y-1 hover:border-blue-200 transition soft-shadow"
              >
                <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50/60">{a.category}</Badge>
                <h3 className="font-display mt-4 text-lg font-semibold text-slate-900 group-hover:text-blue-900 transition leading-snug">
                  {a.title}
                </h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.read_minutes} min read</span>
                  <span className="inline-flex items-center gap-1 text-blue-900 font-medium group-hover:text-emerald-600">
                    Read <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
